// ─── Semaines de repas : quel menu, appliqué ou non ────────────────
// Une ligne par semaine à venir. Le menu s'y affecte (fenêtre de choix), puis
// une bascule l'applique au calendrier — deux gestes séparés pour pouvoir
// préparer ses semaines sans rien poser dans l'agenda tout de suite.
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, TouchableOpacity, ScrollView, StyleSheet, ActivityIndicator } from 'react-native';
import {
  PageHeader, EmptyState, GesteRetourEcran, Bascule, CalqueChoix, LigneChoix,
  useUI, useT, useAuth, frJMAcourt, numeroSemaineISO, confirmerSuppression,
} from '@mop/core';
import { momentsDuMenu } from './services/menus';
import { coursesDesMenus, menusDesSemaines } from './services/coursesMenu';
import { ecouterListeCourses, enregistrerListeCourses, fusionnerDansListe } from './services/listeCourses';
import {
  ecouterSemaines, prochainesSemaines, affecterMenu, appliquerMenu,
  desappliquerMenu, detacherSemaine,
} from './services/semaines';

const NB_SEMAINES = 8;

export default function EcranSemaines({ calId, espaceId, menus = [], config, recettes = [], repasList = [], onFermer }) {
  const UI = useUI();
  const t = useT();
  const { user } = useAuth();
  const s = useMemo(() => makeStyles(UI), [UI]);

  const [semaines, setSemaines] = useState([]);
  const [choixPour, setChoixPour] = useState(null);   // lundi en cours d'affectation
  const [busy, setBusy] = useState(null);             // lundi en cours d'écriture
  const [erreur, setErreur] = useState('');
  const [pourCourses, setPourCourses] = useState(() => new Set());   // lundis coches
  const [liste, setListe] = useState([]);
  const [message, setMessage] = useState('');

  useEffect(() => ecouterSemaines(calId, setSemaines, e => setErreur(e?.message || 'erreur')), [calId]);
  useEffect(() => ecouterListeCourses(espaceId, setListe), [espaceId]);

  const recettesById = useMemo(() => Object.fromEntries(recettes.map(r => [r.id, r])), [recettes]);
  const repasById = useMemo(() => Object.fromEntries(repasList.map(r => [r.id, r])), [repasList]);
  const parLundi = useMemo(() => Object.fromEntries(semaines.map(x => [x.lundi, x])), [semaines]);
  const lignes = useMemo(() => prochainesSemaines(NB_SEMAINES), []);

  const menuDe = (lundi) => menus.find(m => m.id === parLundi[lundi]?.menuId) || null;

  const agir = async (lundi, fn) => {
    setBusy(lundi); setErreur('');
    try { await fn(); } catch (e) { setErreur(e?.message || 'erreur'); }
    setBusy(null);
  };

  const basculer = (lundi, on) => {
    const m = menuDe(lundi);
    if (!m) return;
    agir(lundi, () => (on
      ? appliquerMenu(calId, lundi, m, momentsDuMenu(m, config), recettesById, repasById,
          { uid: user?.uid, nom: user?.prenom || '' })
      : desappliquerMenu(calId, lundi, m.id)));
  };

  // Verse les ingredients des semaines cochees dans la liste de courses. On
  // FUSIONNE (meme nom + meme unite) au lieu d'empiler : deux semaines qui
  // demandent de la farine ne doivent pas faire deux lignes.
  const versCourses = async () => {
    const mens = menusDesSemaines([...pourCourses], semaines, menus);
    const ings = coursesDesMenus(mens, recettesById, repasById);
    if (!ings.length) { setMessage(t('repasPlan.coursesRien')); return; }
    setMessage('');
    try {
      await enregistrerListeCourses(espaceId, fusionnerDansListe(liste, ings));
      setMessage(t('repasPlan.coursesAjoutees', { n: ings.length }));
      setPourCourses(new Set());
    } catch (e) { setErreur(e?.message || 'erreur'); }
  };

  const cocher = (lundi) => setPourCourses(prev => {
    const n = new Set(prev);
    if (n.has(lundi)) n.delete(lundi); else n.add(lundi);
    return n;
  });

  const detacher = (lundi) => confirmerSuppression(
    t('repasPlan.detacherTitre'), t('repasPlan.detacherMsg'),
    () => agir(lundi, () => detacherSemaine(calId, lundi)),
  );

  return (
    <GesteRetourEcran actif={!!onFermer} onRetour={onFermer}>
      <View style={{ flex: 1, backgroundColor: UI.bg }}>
        <PageHeader titre={t('repasPlan.semainesTitre')} iconeTitre="🗓️" onRetour={onFermer}
          action={pourCourses.size
            ? { icone: '🛒', label: t('repasPlan.versCourses'), onPress: versCourses }
            : undefined} />
        <ScrollView contentContainerStyle={s.corps}>
          {!!erreur && <Text style={s.erreur}>⚠️ {erreur}</Text>}
          {!!message && <Text style={s.message}>{message}</Text>}
          {menus.length === 0 ? (
            <EmptyState icon="📆" texte={t('repasPlan.aucun')} aide={t('repasPlan.aucunAide')} />
          ) : lignes.map(({ lundi }) => {
            const sem = parLundi[lundi];
            const m = menuDe(lundi);
            const applique = !!sem?.applique;
            return (
              <View key={lundi} style={[s.carte, applique && s.carteOn]}>
                <View style={s.tete}>
                  <Text style={s.sem}>S.{numeroSemaineISO(new Date(lundi))}</Text>
                  <Text style={s.date}>{frJMAcourt(lundi)}</Text>
                  {busy === lundi && <ActivityIndicator size="small" color={UI.accent} />}
                </View>

                <TouchableOpacity style={s.choix} activeOpacity={0.7} onPress={() => setChoixPour(lundi)}>
                  <Text style={[s.choixTxt, !m && { color: UI.faint }]} numberOfLines={1}>
                    {m ? `📆 ${m.nom}` : t('repasPlan.aucunMenuSemaine')}
                  </Text>
                  <Text style={s.chev}>›</Text>
                </TouchableOpacity>

                {/* La bascule n'a de sens qu'avec un menu affecté : sans lui,
                    il n'y a rien à poser dans le calendrier. */}
                {!!m && (
                  <View style={s.ligneOpt}>
                    <Text style={s.optTxt}>{t('repasPlan.applique')}</Text>
                    <Bascule valeur={applique} onChange={(v) => basculer(lundi, v)}
                      accessibilityLabel={t('repasPlan.applique')} />
                  </View>
                )}
                {!!m && (
                  <TouchableOpacity style={s.ligneOpt} activeOpacity={0.7} onPress={() => cocher(lundi)}>
                    <Text style={s.optTxt}>{t('repasPlan.inclureCourses')}</Text>
                    <Text style={[s.coche, pourCourses.has(lundi) && { color: UI.accent }]}>
                      {pourCourses.has(lundi) ? '☑' : '☐'}
                    </Text>
                  </TouchableOpacity>
                )}
                {!!sem && (
                  <TouchableOpacity onPress={() => detacher(lundi)} style={s.detacher}>
                    <Text style={s.detacherTxt}>{t('repasPlan.detacher')}</Text>
                  </TouchableOpacity>
                )}
              </View>
            );
          })}
        </ScrollView>

        {/* Choix du menu : calque au niveau de l'écran. */}
        <CalqueChoix visible={!!choixPour} emoji="📆" titre={t('repasPlan.choisirMenu')}
          onFermer={() => setChoixPour(null)}>
          {menus.map(m => (
            <LigneChoix key={m.id} libelle={`📆 ${m.nom}`}
              sous={t('repasPlan.nRepas', { n: Object.keys(m.cases || {}).length })}
              onPress={() => { const l = choixPour; setChoixPour(null); agir(l, () => affecterMenu(calId, l, m.id)); }} />
          ))}
        </CalqueChoix>
      </View>
    </GesteRetourEcran>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  corps: { padding: 12, paddingBottom: 30 },
  carte: { backgroundColor: UI.card, borderRadius: 14, borderWidth: 0.5, borderColor: UI.border, padding: 12, marginBottom: 9 },
  carteOn: { borderColor: UI.accent, borderWidth: 1 },
  tete: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  sem: { fontSize: 14, fontWeight: '800', color: UI.ink },
  date: { flex: 1, fontSize: 12, color: UI.muted },
  choix: { flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 9, paddingVertical: 10, paddingHorizontal: 11,
           backgroundColor: UI.champ, borderRadius: 10, borderWidth: 0.5, borderColor: UI.champBord },
  choixTxt: { flex: 1, fontSize: 14, fontWeight: '700', color: UI.ink },
  chev: { fontSize: 17, color: UI.accent, fontWeight: '800' },
  ligneOpt: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 10 },
  optTxt: { fontSize: 13.5, color: UI.ink, fontWeight: '700' },
  detacher: { alignItems: 'center', marginTop: 8 },
  detacherTxt: { fontSize: 12, color: UI.danger || '#C62828', fontWeight: '700' },
  erreur: { fontSize: 12.5, color: UI.danger || '#C62828', fontWeight: '700', marginBottom: 10 },
  message: { fontSize: 12.5, color: UI.accent, fontWeight: '700', marginBottom: 10 },
  coche: { fontSize: 20, color: UI.faint },
});
